/// <reference lib="dom" />
/**
 * Input abstraction (brief §5.3): pointer/touch + keyboard, one-handed
 * portrait first. Produces quantised int16 axes and the shared bit layout —
 * floats never reach the sim. Choice answers (from the shell overlay or keys
 * 1–3) are injected as CHOICE_INDEX bits on the next sampled tick.
 */

import type { InputFrame } from '@hnplay/shared';
import { INPUT_BIT_PRIMARY, INPUT_BIT_SECONDARY, withChoice } from '@hnplay/shared';
import type { InputSource } from './loop.js';

const KEY_CURSOR_STEP = 1100; // int16 units per tick while an arrow is held
// 'stick' mode: deflect fast while held, spring back to centre on release.
const KEY_STICK_STEP = 6000;
const KEY_STICK_RETURN = 8000;

export interface PointerKeyboardInput extends InputSource {
  /** Overlay buttons call this with 1..3. */
  selectChoice(n: number): void;
  attach(): void;
  detach(): void;
  /**
   * §6.4: DeviceOrientation steering. iOS requires this be called from a user
   * gesture (the shell's pre-run button). Resolves false when unavailable or
   * denied — pointer/keyboard remain the fallback, never a dead end.
   */
  enableOrientation(): Promise<boolean>;
  readonly orientationActive: boolean;
}

export function createPointerKeyboardInput(
  target: HTMLElement,
  keyboardMode: 'cursor' | 'stick' = 'cursor',
): PointerKeyboardInput {
  let ax = 0;
  let ay = 0;
  let keyAx = 0;
  let keyAy = 0;
  let primary = false;
  let secondary = false;
  let pendingChoice = 0;
  const keys = new Set<string>();
  let orientationOn = false;
  let betaNeutral: number | null = null;
  let orientAx = 0;
  let orientAy = 0;

  function onOrientation(e: DeviceOrientationEvent): void {
    if (e.gamma === null || e.beta === null) return;
    // gamma: left/right lean, ±40° = full deflection; beta relative to the
    // holding angle captured on the first reading (people don't play flat).
    if (betaNeutral === null) betaNeutral = e.beta;
    const gx = Math.max(-40, Math.min(40, e.gamma)) / 40;
    const gy = Math.max(-30, Math.min(30, e.beta - betaNeutral)) / 30;
    orientAx = Math.round(gx * 32767);
    orientAy = Math.round(gy * 32767);
  }

  function quantise(v: number): number {
    const q = Math.round(v * 32767);
    return q > 32767 ? 32767 : q < -32768 ? -32768 : q;
  }

  /**
   * Overlay UI (choice cards, the tilt permission gate, HUD buttons) lives INSIDE
   * the same element we listen on. Capturing the pointer for those presses
   * retargets the click event to the overlay, so the button's own click handler
   * never fires — on a mouse that made every in-overlay button dead (touch was
   * unaffected: it gets implicit capture on the real target). Let the UI have
   * its clicks, and never feed them to the sim as a play press either.
   */
  function isUiTarget(t: EventTarget | null): boolean {
    return t instanceof Element && t.closest('button, a, input, select, textarea, label') !== null;
  }

  function onPointer(e: PointerEvent): void {
    const rect = target.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    ax = quantise(((e.clientX - rect.left) / rect.width) * 2 - 1);
    ay = quantise(((e.clientY - rect.top) / rect.height) * 2 - 1);
  }
  function onPointerDown(e: PointerEvent): void {
    if (isUiTarget(e.target)) return;
    onPointer(e);
    primary = true;
    target.setPointerCapture?.(e.pointerId);
  }
  function onPointerUp(e: PointerEvent): void {
    if (isUiTarget(e.target) && !primary) return;
    onPointer(e);
    primary = false;
  }
  function onKeyDown(e: KeyboardEvent): void {
    // Before the repeat gate — held keys keep firing repeats, and those must
    // not scroll the page mid-run either.
    if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault();
    if (e.repeat) return;
    keys.add(e.code);
    if (e.code === 'Space') primary = true;
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') secondary = true;
    if (e.code === 'Digit1') pendingChoice = 1;
    if (e.code === 'Digit2') pendingChoice = 2;
    if (e.code === 'Digit3') pendingChoice = 3;
  }
  function onKeyUp(e: KeyboardEvent): void {
    keys.delete(e.code);
    if (e.code === 'Space') primary = false;
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') secondary = false;
  }

  return {
    get orientationActive(): boolean {
      return orientationOn;
    },
    async enableOrientation(): Promise<boolean> {
      interface DOEStatic {
        requestPermission?: () => Promise<string>;
      }
      const DOE = (globalThis as { DeviceOrientationEvent?: DOEStatic }).DeviceOrientationEvent;
      if (!DOE) return false;
      try {
        if (typeof DOE.requestPermission === 'function') {
          const result = await DOE.requestPermission();
          if (result !== 'granted') return false;
        }
      } catch {
        return false;
      }
      betaNeutral = null;
      window.addEventListener('deviceorientation', onOrientation);
      orientationOn = true;
      return true;
    },
    sample(): InputFrame {
      let outAx: number;
      let outAy: number;
      if (keyboardMode === 'stick') {
        // Deflect toward full while held; spring back to centre when released.
        const stick = (held: -1 | 0 | 1, v: number): number => {
          if (held !== 0) return Math.max(-32768, Math.min(32767, v + held * KEY_STICK_STEP));
          if (v > 0) return Math.max(0, v - KEY_STICK_RETURN);
          return Math.min(0, v + KEY_STICK_RETURN);
        };
        const hx = keys.has('ArrowLeft') ? -1 : keys.has('ArrowRight') ? 1 : 0;
        const hy = keys.has('ArrowUp') ? -1 : keys.has('ArrowDown') ? 1 : 0;
        keyAx = stick(hx, keyAx);
        keyAy = stick(hy, keyAy);
        // While the spring is live the keyboard owns the axes; otherwise the
        // pointer's absolute position steers as before.
        const springLive = hx !== 0 || hy !== 0 || keyAx !== 0 || keyAy !== 0;
        outAx = springLive ? keyAx : ax;
        outAy = springLive ? keyAy : ay;
      } else {
        // 'cursor': arrows move the persistent virtual cursor at a fixed rate.
        if (keys.has('ArrowLeft')) ax = Math.max(-32768, ax - KEY_CURSOR_STEP);
        if (keys.has('ArrowRight')) ax = Math.min(32767, ax + KEY_CURSOR_STEP);
        if (keys.has('ArrowUp')) ay = Math.max(-32768, ay - KEY_CURSOR_STEP);
        if (keys.has('ArrowDown')) ay = Math.min(32767, ay + KEY_CURSOR_STEP);
        outAx = ax;
        outAy = ay;
      }
      if (orientationOn) {
        outAx = orientAx;
        outAy = orientAy;
      }

      let bits = 0;
      if (primary) bits |= INPUT_BIT_PRIMARY;
      if (secondary) bits |= INPUT_BIT_SECONDARY;
      if (pendingChoice > 0) {
        bits = withChoice(bits, pendingChoice);
        pendingChoice = 0;
      }
      return { bits, axisX: outAx, axisY: outAy };
    },
    selectChoice(n: number): void {
      if (n >= 1 && n <= 3) pendingChoice = n;
    },
    attach(): void {
      target.addEventListener('pointermove', onPointer);
      target.addEventListener('pointerdown', onPointerDown);
      target.addEventListener('pointerup', onPointerUp);
      target.addEventListener('pointercancel', onPointerUp);
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup', onKeyUp);
    },
    detach(): void {
      target.removeEventListener('pointermove', onPointer);
      target.removeEventListener('pointerdown', onPointerDown);
      target.removeEventListener('pointerup', onPointerUp);
      target.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('deviceorientation', onOrientation);
      orientationOn = false;
    },
  };
}